// First, before any other module can touch the browser: the Erklärung's embedded instance gets its own
// storage, cookies and channels, so nothing it does reaches the reader's own session.
import "@/services/viewHelp/isolation"
import { createApp } from "vue"
import App from "./App.vue"
import router from "./router"
import { createStore } from "./store"
import { i18n } from "./plugins/i18n/index"
import VueDatePicker from "./plugins/vueDatePicker"
import vuetify from "./plugins/vuetify"
import VueTelInput from "./plugins/vueTelInput"
import "./plugins/leaflet"
import initializeSentry from "./plugins/sentry"
import "unfonts.css"
import "./styles/globals.scss"
import "./styles/vuetify3-compat.scss"
import { auth } from "@/services/auth"
import { useSettingsStore } from "@/store/settings"
import { useAuthStore } from "@/store/authentication"
import { useSubscriptionStore } from "@/store/subscription"
import { useTimeTrackingStore } from "@/store/timetracking"
import { isEntitled } from "@/services/entitlements"
import { FEATURE_SETS } from "@/services/featureSet"
import { hasPermissionsOfAll } from "@/services/permissionService"
import { PERMISSIONS } from "@/services/permission"
import { a11yPlugin } from "@/services/a11y.ts"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"
import { installNetworkGuard } from "@/services/viewHelp/guard"
import { apiClient } from "@/services/api"

/*
 * The instance inside the Erklärung's frame is the real app, signed in as the reader — so before it
 * sends its first request, it is sealed: nothing it does can write, and the lists that would show
 * real people and places answer with sample data. See services/viewHelp/sandbox.ts.
 */
if (IS_VIEW_HELP_EMBED) installNetworkGuard()

/**
 * The rest of the seal, loaded only inside the frame: the sandbox with the sample company, and the
 * sample "zuletzt besucht". A dynamic import, so no other session downloads the sample world — awaited
 * at the top of `init`, before its first request.
 */
async function sealEmbeddedInstance() {
  if (!IS_VIEW_HELP_EMBED) return
  const [{ installViewHelpSandbox }, { sampleRecentEntities }, { provideEmbeddedRecentEntities }] = await Promise.all([
    import("@/services/viewHelp/sandbox"),
    import("@/services/viewHelp/samples/recents"),
    import("@/store/recentEntities"),
  ])
  installViewHelpSandbox(apiClient)
  provideEmbeddedRecentEntities(sampleRecentEntities())
}

export const app = createApp(App)

async function init() {
  await sealEmbeddedInstance()
  // Initially load setting and principal data if user is authenticated
  if (auth.isLoggedIn()) {
    const settingsStore = useSettingsStore()
    await settingsStore.loadSettings()
    const authStore = useAuthStore()
    await authStore.loadPrincipal()
    // Load subscription status before mount so the subscription guard has state on first navigation.
    await useSubscriptionStore().loadStatus()

    /*
     * The running clock-in behind the toolbar's chip.
     *
     * Here and not in `LewToolBar`, although that is what draws it: every view wraps its own
     * `LewBaseLayout`, so the toolbar remounts on every navigation and a load in its `onMounted`
     * would be a request per page — the one thing the chip must not do. Here it happens once per
     * session, and the clock actions keep it current from then on. This branch is the only place in
     * the app that already knows there *is* a session, which is the other half of it: an
     * unauthenticated visitor never reaches this line, and no page renders the toolbar for them.
     *
     * Gated on the add-on, not merely awaited: `/time-tracking/**` answers 403 FEATURE_NOT_ENTITLED
     * for a workspace that never bought Zeiterfassung, and the interceptor turns that into a
     * redirect to the upsell page — so an ungated load here would greet those workspaces with a
     * sales pitch instead of their dashboard. `loadPrincipal` above is what fills the entitlements
     * this reads.
     *
     * Behind RECORD_OWN_TIME as well: `/time-tracking/me/**` requires it, so for a role kept out of
     * recording this would be a guaranteed 403 per session for a chip they can never have.
     *
     * Not awaited, and silent: nothing on screen depends on it, so it must not hold up the mount,
     * and a failure leaves `running` null — the chip stays away and the toolbar is unaffected.
     */
    if (isEntitled(FEATURE_SETS.TIME_TRACKING)) {
      const timeTracking = useTimeTrackingStore()

      /*
       * The instance inside the Erklärung's frame shows a curated sample week instead of whatever
       * this workspace has recorded — see `services/viewHelp/demo.ts`. Set before the mount, so the
       * frame never paints a real (or empty) screen first, and before the prefetches below, which
       * `enterHelpDemo` turns inert: the frame is a throwaway instance and every request it makes is
       * one the user did not ask for. Awaited: it loads the sample week with a dynamic import, so no
       * other session downloads it.
       */
      if (IS_VIEW_HELP_EMBED) {
        await timeTracking.enterHelpDemo()
        return
      }

      if (hasPermissionsOfAll([PERMISSIONS.RECORD_OWN_TIME]).value) {
        timeTracking.loadRunning(true).catch(() => null)
      }

      /*
       * The two queue sizes the Zeiterfassung's tab strip badges: who is behind on recording, and
       * what broke a rule.
       *
       * Here for the same reason as the clock above, twice over. The strip renders on all six pages
       * of the feature, so a load inside it would be six requests per navigation — and a count that
       * only its own page loads is a badge that appears exactly where nobody needs it and vanishes on
       * the way to the page they would have acted from. Once per session, into shared state the strip
       * reads. What window and whose employees it covers is decided in `loadNavCounts`.
       *
       * Behind the permission as well as the add-on: `/time-tracking/coverage` and
       * `/time-tracking/compliance` are both VIEW_TIME_ENTRY_FOREIGN, and both tabs are dropped from
       * the strip without it — so for the rest of the staff this would be two guaranteed 403s per
       * page load for a badge they never see.
       *
       * Not awaited, and silent throughout: nothing on screen depends on it, so it must not hold up
       * the mount, and a failure leaves both counts at zero — which the strip draws as no badge, the
       * same as a workspace with nothing to do.
       */
      if (hasPermissionsOfAll([PERMISSIONS.VIEW_TIME_ENTRY_FOREIGN]).value) {
        timeTracking.loadNavCounts().catch(() => null)
      }
    }
  }
}

;(async () => {
  initializeSentry(app)
  const store = createStore()
  app.use(store)

  await init()
  app.use(router)
  router.app = app // https://router.vuejs.org/guide/migration/#Removal-of-router-app
  app.use(i18n)
  app.use(vuetify)
  app.use(VueTelInput)
  app.use(a11yPlugin)
  app.component("VueDatePicker", VueDatePicker)
  app.mount("#app")
})()
